/**
 * Authentication Service - Updated to use new AuthRepository
 * Maintains existing interface while using new AuthRepository internally
 */

import { AuthRepository } from '../api/data/repositories/auth/authRepository';
import { trackEvent } from '../utils/analytics';

// Create AuthRepository instance immediately to ensure auth state listener is set up
let authRepositoryInstance = null;
let authInitialized = false;
let authInitializationPromise = null;

// Initialize AuthRepository
const getAuthRepository = () => {
  if (!authRepositoryInstance) {
    // Create AuthRepository without logger
    authRepositoryInstance = new AuthRepository(null, null);
  }
  return authRepositoryInstance;
};

// Initialize the AuthRepository immediately when this module loads
// This ensures the auth state listener is set up as soon as possible
const initializeAuthRepository = () => {
  if (!authInitializationPromise) {
    authInitializationPromise = new Promise((resolve) => {
      const authRepo = getAuthRepository();

      // Wait for Firebase auth to initialize
      const unsubscribe = authRepo.auth.onAuthStateChanged((user) => {
        if (!authInitialized) {
          authInitialized = true;
          resolve();
          unsubscribe();
        }
      });

      // Fallback timeout in case auth state never changes
      setTimeout(() => {
        if (!authInitialized) {
          authInitialized = true;
          resolve();
          unsubscribe();
        }
      }, 3000);
    });
  }
  return authInitializationPromise;
};

// Call initialization immediately
initializeAuthRepository();

// Note: No more static API key fallback - all API keys come from Firestore

/**
 * Helper function to clear stored data
 */
const clearStoredData = () => {
  // Clear all localStorage items
  localStorage.clear();
  
  // Clear all sessionStorage items
  sessionStorage.clear();
  
  // Clear any cookies (if they exist)
  document.cookie.split(";").forEach(function(c) { 
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
  });
};

/**
 * Store user information in localStorage
 * @param {Object} user - The Firebase auth user object
 */
const storeUserInfo = (user) => {

  if (user) {
    // Store user name (use display name or email as fallback)
    localStorage.setItem('userName', user.displayName || user.email?.split('@')[0] || 'User');

    // Store user email
    if (user.email) {
      localStorage.setItem('userEmail', user.email);
    } else {
    }

    // Store photoURL if available
    if (user.photoURL) {
      localStorage.setItem('userPhotoURL', user.photoURL);
    }

    // Store user ID
    localStorage.setItem('userId', user.uid);
  } else {
  }
};

export const authService = {
  // Sign in with email and password - Enhanced with proper API key management
  signInWithEmail: async (email, password) => {
    try {
      const authRepo = getAuthRepository();
      const result = await authRepo.signInWithEmail(email, password);

      if (result.success) {

        // Store user info for backward compatibility
        storeUserInfo(result.user);

        // Handle API key based on retrieval result
        if (result.apiKey) {
          // API key is already stored in localStorage by AuthRepository
        } else if (result.apiKeyError) {

          // Provide specific error handling based on the type of API key error
          if (result.apiKeyError.includes('User document not found')) {
          } else if (result.apiKeyError.includes('API key field not found')) {
          } else if (result.apiKeyError.includes('API key is empty')) {
          } else if (result.apiKeyError.includes('timed out')) {
          } else {
          }

          // Don't store any fallback API key - user must have valid API key in Firestore
        } else {
        }


        // Return user data with first_login status for routing decisions
        return {
          user: result.user,
          firstLogin: result.userData?.first_login || false,
          userData: result.userData
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {

      // Handle specific error messages from AuthRepository
      if (error.message.includes('auth/invalid-credential') || error.message.includes('Invalid email or password')) {
        throw new Error('auth/invalid-credential');
      } else if (error.message.includes('Too many')) {
        throw new Error('Too many unsuccessful login attempts. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to sign in. Please try again.');
      }
    }
  },

  // Sign in with Google - Enhanced with proper API key management
  signInWithGoogle: async () => {
    try {
      const authRepo = getAuthRepository();
      const result = await authRepo.signInWithGoogle();

      if (result.success) {
        // Store user info for backward compatibility
        storeUserInfo(result.user);

        // Handle API key based on retrieval result
        if (result.apiKey) {
          // API key is already stored in localStorage by AuthRepository
        } else if (result.apiKeyError) {
          // Provide specific error handling based on the type of API key error
          if (result.apiKeyError.includes('User document not found')) {
          } else if (result.apiKeyError.includes('API key field not found')) {
          } else if (result.apiKeyError.includes('API key is empty')) {
          } else if (result.apiKeyError.includes('timed out')) {
          } else {
          }
          // Don't store any fallback API key - user must have valid API key in Firestore
        } else {
        }

        // Return user data with first_login status for routing decisions
        return {
          user: result.user,
          firstLogin: result.userData?.first_login || false,
          userData: result.userData
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      if (error.message.includes('cancelled')) {
        throw new Error('Sign-in was cancelled.');
      } else if (error.message.includes('popup')) {
        throw new Error('Pop-up was blocked. Please allow pop-ups and try again.');
      } else if (error.message.includes('auth/invalid-credential')) {
        throw new Error('auth/invalid-credential');
      } else {
        throw new Error(error.message || 'Failed to sign in with Google.');
      }
    }
  },

  // Sign out - Enhanced with comprehensive API key cleanup
  signOut: async () => {
    try {
      const authRepo = getAuthRepository();
      const result = await authRepo.signOut();

      if (result.success) {
        // Track successful logout
        trackEvent('logout', {
          reason: 'user_initiated',
          success: true
        });

        // Additional cleanup at service level (redundant but safe)
        clearStoredData();

        // Verify API key cleanup
        const remainingApiKey = localStorage.getItem('PROVIDER_API_KEY');
        if (remainingApiKey) {
          localStorage.removeItem('PROVIDER_API_KEY');
        }


        // Redirect to landing page
        window.location.href = "/";
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      // Track failed logout
      trackEvent('logout', {
        reason: 'user_initiated',
        success: false,
        error_message: error.message
      });

      // Emergency cleanup - still clear data and redirect even if sign out fails
      clearStoredData();

      // Force remove API key
      localStorage.removeItem('PROVIDER_API_KEY');


      window.location.href = "/";
      throw new Error('Failed to sign out. Please try again.');
    }
  },

  // Get current user - Updated to use AuthRepository
  getCurrentUser: () => {
    const authRepo = getAuthRepository();
    return authRepo.getCurrentUser();
  },

  // Check if user is logged in - Enhanced to require both Firebase auth AND API key
  isLoggedIn: () => {
    const authRepo = getAuthRepository();
    const currentUser = authRepo.getCurrentUser();

    // User must be authenticated via Firebase
    if (!currentUser) {
      return false;
    }

    // User must also have a valid API key for complete authentication
    const apiKey = localStorage.getItem("PROVIDER_API_KEY");
    if (!apiKey || apiKey.trim() === '') {
      return false;
    }

    return true;
  },

  // Get current API key - Safe method that doesn't trigger Firestore calls
  getCurrentApiKey: () => {
    // First check localStorage (fastest and doesn't require authentication)
    const localStorageKey = localStorage.getItem("PROVIDER_API_KEY");
    if (localStorageKey) {
      return localStorageKey;
    }

    // Only check AuthRepository if user is authenticated
    const authRepo = getAuthRepository();
    const currentUser = authRepo.getCurrentUser();

    if (currentUser) {
      // User is authenticated, safe to get cached API key from repository
      const cachedKey = authRepo.getCurrentApiKey();
      if (cachedKey) {
        return cachedKey;
      }
    }

    // No fallback - return null if no API key is available
    // This forces proper error handling in API calls
    return null;
  },

  // Validate token - New method using AuthRepository
  validateToken: async () => {
    try {
      const authRepo = getAuthRepository();
      return await authRepo.validateToken();
    } catch (error) {
      return { valid: false, error: error.message };
    }
  },

  // Check authentication status with detailed information
  getAuthStatus: () => {
    const authRepo = getAuthRepository();
    const currentUser = authRepo.getCurrentUser();
    const apiKey = localStorage.getItem("PROVIDER_API_KEY");

    return {
      hasFirebaseAuth: !!currentUser,
      hasApiKey: !!(apiKey && apiKey.trim() !== ''),
      isFullyAuthenticated: !!currentUser && !!(apiKey && apiKey.trim() !== ''),
      user: currentUser,
      apiKeyLength: apiKey ? apiKey.length : 0
    };
  },

  // Wait for auth initialization
  waitForAuthInit: () => {
    return authInitializationPromise || Promise.resolve();
  },

  // Check if auth is initialized
  isAuthInitialized: () => {
    return authInitialized;
  }
};
